import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, X, Upload, Image, MapPin, Search, Wand2, Sparkles, Sun, Camera, Smartphone, Home as HomeIcon, Maximize2, AlertTriangle } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { useCurrencySettings } from '../utils/currency';
import PhotoBridge from '../components/PhotoBridge';
import AIFloorPlanGenerator from '../components/AIFloorPlanGenerator';
import logger from '../utils/logger';

// Fix leaflet default icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Map click handler component
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const DEFAULT_MAP_ZOOM = 15;

// Fly to a specific location
const FlyToLocation = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, DEFAULT_MAP_ZOOM, { duration: 1.5 });
  }, [position, map]);
  return null;
};

// Map Search Bar Component
const MapSearchBar = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const searchLocation = async (q) => {
    if (q.length < 3) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=5`);
      const data = await res.json();
      setResults(data);
    } catch (e) { logger.error(e); }
    finally { setSearching(false); }
  };

  const handleInput = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchLocation(val), 400);
  };

  return (
    <div className="relative">
      <div className="flex items-center bg-white border border-slate-200 overflow-hidden">
        <Search className="h-4 w-4 text-slate-400 ml-3 flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          placeholder="Search location... e.g. London, Manchester"
          className="w-full px-3 py-3 text-sm text-[#1A1A1A] bg-transparent outline-none placeholder:text-slate-400"
          data-testid="map-search-input"
        />
        {searching && <Loader2 className="h-4 w-4 text-[#E07A3A] animate-spin mr-3 flex-shrink-0" />}
      </div>
      {results.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 border-t-0 z-[1000] max-h-48 overflow-y-auto shadow-lg">
          {results.map((r) => (
            <button
              key={r.place_id}
              type="button"
              className="w-full text-left px-4 py-3 hover:bg-[#FEF3EC] text-sm text-[#1A1A1A] border-b border-slate-100 last:border-0 flex items-start gap-2"
              onClick={() => {
                onSelect(parseFloat(r.lat), parseFloat(r.lon));
                setQuery(r.display_name.split(',').slice(0, 3).join(', '));
                setResults([]);
              }}
              data-testid={`search-result-${r.place_id}`}
            >
              <MapPin className="h-4 w-4 text-[#E07A3A] mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const getEpcLabel = (rating) => {
  if (rating <= 'B') return '(Very Efficient)';
  if (rating <= 'D') return '(Average)';
  return '(Below Average)';
};

const CreatePropertyPage = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEditing = !!editId;
  const { currency, symbol } = useCurrencySettings();
  const { user } = useAuth();
  const isAgent = user?.role === 'agent';
  const dashboardPath = isAgent ? '/agent' : '/seller';
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showImageTips, setShowImageTips] = useState(false);
  const [imageTips, setImageTips] = useState(null);
  const imageInputRef = useRef(null);
  const panoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '', description: '', property_type: '', bedrooms: '', bathrooms: '',
    area_sqft: '', price: '', address: '', city: '', state: '', zip_code: '', country: '',
    latitude: '', longitude: '', images: [], panoramic_image: '', floor_plan_images: [],
    floor_plans: [],
    amenities: [], listing_type: 'sale',
    tenure: '', lease_years_remaining: '', ground_rent: '', service_charge: '',
    epc_rating: '', council_tax_band: '', furnished_status: '', deposit_amount: '', available_from: '',
    owner_name: '', owner_email: '', owner_phone: '',
    // Plot / Land / Commercial specific
    plot_area: '', plot_area_unit: 'sqft', plot_dimensions: '',
    approval_authority: '', survey_number: '', khata_number: '',
    zoning: '', facing: ''
  });
  const [newAmenity, setNewAmenity] = useState('');
  const floorPlanInputRef = useRef(null);

  // Load existing property for editing
  useEffect(() => {
    if (editId) {
      const loadProperty = async () => {
        try {
          const res = await axios.get(`${API_URL}/api/properties/${editId}`);
          const p = res.data;
          setFormData({
            title: p.title || '', description: p.description || '',
            property_type: p.property_type || '', bedrooms: p.bedrooms?.toString() || '',
            bathrooms: p.bathrooms?.toString() || '', area_sqft: p.area_sqft?.toString() || '',
            price: p.price?.toString() || '', address: p.address || '', city: p.city || '',
            state: p.state || '', zip_code: p.zip_code || '', country: p.country || '',
            latitude: p.latitude?.toString() || '', longitude: p.longitude?.toString() || '',
            images: p.images || [], panoramic_image: p.panoramic_image || '',
            floor_plan_images: p.floor_plan_images || [],
            floor_plans: p.floor_plans || [],
            amenities: p.amenities || [], listing_type: p.listing_type || 'sale',
            tenure: p.tenure || '', lease_years_remaining: p.lease_years_remaining?.toString() || '',
            ground_rent: p.ground_rent?.toString() || '', service_charge: p.service_charge?.toString() || '',
            epc_rating: p.epc_rating || '', council_tax_band: p.council_tax_band || '',
            furnished_status: p.furnished_status || '', deposit_amount: p.deposit_amount?.toString() || '',
            available_from: p.available_from || '',
            owner_name: p.owner_name || '', owner_email: p.owner_email || '', owner_phone: p.owner_phone || '',
            plot_area: p.plot_area?.toString() || '', plot_area_unit: p.plot_area_unit || 'sqft',
            plot_dimensions: p.plot_dimensions || '', approval_authority: p.approval_authority || '',
            survey_number: p.survey_number || '', khata_number: p.khata_number || '',
            zoning: p.zoning || '', facing: p.facing || ''
          });
          if (p.latitude && p.longitude) {
            setFlyTarget([parseFloat(p.latitude), parseFloat(p.longitude)]);
          }
        } catch (error) {
          toast.error('Failed to load property');
          navigate(dashboardPath);
        }
      };
      loadProperty();
    }
  }, [editId, navigate, dashboardPath]);

  const handleChange = (key, value) => setFormData({ ...formData, [key]: value });

  // Reverse geocode from map click
  const handleMapClick = useCallback(async (lat, lng) => {
    setFormData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
    setReverseLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`);
      const data = await res.json();
      if (data.address) {
        const addr = data.address;
        setFormData(prev => ({
          ...prev,
          latitude: lat.toString(),
          longitude: lng.toString(),
          address: [addr.road, addr.neighbourhood, addr.suburb].filter(Boolean).join(', ') || prev.address,
          city: addr.city || addr.town || addr.village || addr.county || prev.city,
          state: addr.state || prev.state,
          zip_code: addr.postcode || prev.zip_code,
          country: addr.country || prev.country,
        }));
        toast.success('Location set! Address auto-filled.');
      }
    } catch (e) {
      logger.error('Reverse geocoding failed:', e);
    } finally { setReverseLoading(false); }
  }, []);

  // Search selected -> fly to location and reverse geocode
  const handleSearchSelect = useCallback((lat, lng) => {
    setFlyTarget([lat, lng]);
    handleMapClick(lat, lng);
  }, [handleMapClick]);

  const addAmenity = () => {
    if (newAmenity.trim()) {
      setFormData({ ...formData, amenities: [...formData.amenities, newAmenity.trim()] });
      setNewAmenity('');
    }
  };
  const removeAmenity = (i) => setFormData({ ...formData, amenities: formData.amenities.filter((_, idx) => idx !== i) });
  const removeImage = (i) => setFormData({ ...formData, images: formData.images.filter((_, idx) => idx !== i) });

  const handleBridgePhotos = (urls) => {
    if (urls.length > 0) {
      setFormData(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      toast.success(`${urls.length} photo${urls.length !== 1 ? 's' : ''} added from phone`);
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      const formPayload = new FormData();
      files.forEach(f => formPayload.append('files', f));
      const res = await axios.post(`${API_URL}/api/upload/multiple`, formPayload);
      const items = Array.isArray(res.data) ? res.data : (res.data.items || []);
      const rejected = Array.isArray(res.data) ? [] : (res.data.rejected || []);
      const urls = items.map(r => r.cloud ? r.url : `${API_URL}${r.url}`);
      setFormData(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      if (urls.length > 0) {
        toast.success(`${urls.length} image(s) uploaded`);
      }
      rejected.forEach(r => {
        toast.error(r.message, { duration: 12000 });
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        toast.error(detail.message, { duration: 12000 });
      } else {
        toast.error('Upload failed');
      }
    } finally { setUploading(false); }
  };

  const handlePanoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formPayload = new FormData();
      formPayload.append('file', file);
      const res = await axios.post(`${API_URL}/api/upload`, formPayload);
      const imageUrl = res.data.cloud ? res.data.url : `${API_URL}${res.data.url}`;
      setFormData(prev => ({ ...prev, panoramic_image: imageUrl }));
      toast.success('360° image uploaded');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        toast.error(detail.message, { duration: 12000 });
      } else {
        toast.error('Upload failed');
      }
    } finally { setUploading(false); }
  };

  const generateAiDescription = async () => {
    if (!formData.title && !formData.property_type) {
      toast.error('Fill in the property title and type first');
      return;
    }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-description`, {
        title: formData.title,
        property_type: formData.property_type,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        area_sqft: formData.area_sqft,
        price: formData.price,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        amenities: formData.amenities,
        listing_type: formData.listing_type
      });
      handleChange('description', res.data.description);
      toast.success('AI description generated!');
    } catch (error) {
      toast.error('Failed to generate description');
    } finally {
      setAiGenerating(false);
    }
  };

  const loadImageTips = async () => {
    if (imageTips) { setShowImageTips(!showImageTips); return; }
    try {
      const res = await axios.get(`${API_URL}/api/ai/image-tips`);
      setImageTips(res.data);
      setShowImageTips(true);
    } catch (e) {
      toast.error('Failed to load tips');
    }
  };

  const handleFloorPlanUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setFormData(prev => ({ ...prev, floor_plan_images: [...prev.floor_plan_images, res.data.url] }));
      }
      toast.success('Floor plan uploaded');
    } catch (error) {
      toast.error('Failed to upload floor plan');
    } finally {
      setUploading(false);
      if (floorPlanInputRef.current) floorPlanInputRef.current.value = '';
    }
  };



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.price || !formData.city) { toast.error('Fill required fields'); return; }
    const isPlotType = formData.property_type === 'land' || formData.property_type === 'commercial';
    if (isPlotType && !formData.plot_area) {
      toast.error('Please enter the plot area (required for land/commercial listings).');
      return;
    }
    setLoading(true);
    try {
      const plotAreaNum = parseFloat(formData.plot_area) || 0;
      // If it's a plot and area_sqft is empty, normalize plot_area → area_sqft for search compatibility
      const normalizedAreaSqft = (() => {
        const raw = parseFloat(formData.area_sqft);
        if (raw > 0) return raw;
        if (!isPlotType || !plotAreaNum) return 0;
        const unit = formData.plot_area_unit || 'sqft';
        const toSqft = { sqft: 1, sqyd: 9, sqm: 10.7639, acre: 43560, hectare: 107639 };
        return Math.round(plotAreaNum * (toSqft[unit] || 1));
      })();

      const payload = {
        ...formData,
        // Plots don't have bedrooms/bathrooms — force to 0 instead of leaking empty strings.
        bedrooms: isPlotType ? 0 : (parseInt(formData.bedrooms) || 0),
        bathrooms: isPlotType ? 0 : (parseInt(formData.bathrooms) || 0),
        area_sqft: normalizedAreaSqft,
        price: parseFloat(formData.price),
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        lease_years_remaining: formData.lease_years_remaining ? parseInt(formData.lease_years_remaining) : null,
        ground_rent: formData.ground_rent ? parseFloat(formData.ground_rent) : null,
        service_charge: formData.service_charge ? parseFloat(formData.service_charge) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        tenure: formData.tenure || null,
        epc_rating: formData.epc_rating || null,
        council_tax_band: formData.council_tax_band || null,
        furnished_status: formData.furnished_status || null,
        available_from: formData.available_from || null,
        plot_area: plotAreaNum || null,
        plot_area_unit: isPlotType ? (formData.plot_area_unit || 'sqft') : null,
        plot_dimensions: formData.plot_dimensions || null,
        approval_authority: formData.approval_authority || null,
        rera_number: (formData.rera_number || '').trim() || null,
        rera_exempt: !!formData.rera_exempt,
        survey_number: formData.survey_number || null,
        khata_number: formData.khata_number || null,
        zoning: formData.zoning || null,
        facing: formData.facing || null,
      };
      await (isEditing
        ? axios.put(`${API_URL}/api/properties/${editId}`, payload)
        : axios.post(`${API_URL}/api/properties`, payload)
      );
      toast.success(isEditing ? 'Property updated!' : 'Property listed!');
      navigate(dashboardPath);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button onClick={() => navigate(dashboardPath)} className="inline-flex items-center text-[#1A1A1A]/40 hover:text-[#1A1A1A] mb-8 text-sm tracking-wider font-medium" data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-2" />BACK TO DASHBOARD
        </button>

        <div className="mb-10">
          <span className="text-sm tracking-[0.3em] text-[#E07A3A] mb-2 block uppercase font-semibold">{isEditing ? 'Edit Listing' : 'New Listing'}</span>
          <h1 className="font-['Playfair_Display'] text-4xl text-[#1A1A1A] font-semibold">{isEditing ? 'Update Property' : 'Create Property'}</h1>
          <p className="text-[#1A1A1A]/60 mt-2 text-base font-medium">List your property. Prices in {currency} ({symbol}).</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="luxury-card p-6 space-y-5">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Basic Information</h2>
            <div>
              <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Title *</Label>
              <Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Luxury 3-Bed in Central London"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1 text-base" required data-testid="input-title" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Description</Label>
                <button
                  type="button"
                  onClick={generateAiDescription}
                  disabled={aiGenerating}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#E07A3A] hover:text-[#C4652E] transition-colors disabled:opacity-50"
                  data-testid="ai-generate-desc-btn"
                >
                  {aiGenerating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
                  {aiGenerating ? 'Generating...' : 'AI Write for Me'}
                </button>
              </div>
              <Textarea value={formData.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Describe your property... or click 'AI Write for Me' above"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] mt-1 min-h-[120px] text-base" data-testid="input-description" />
              <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-3 py-2 leading-relaxed" data-testid="pii-notice">
                <strong>Direct phone numbers or contact details are NOT allowed</strong> — neither in this description, nor in the photos you upload. Listings or photos that contain phone numbers, emails, WhatsApp/Telegram handles, or "call me / direct owner" text will be rejected with an error explaining what was found. Buyers reach you only after they pay the introduction fee. This is how Shopperden protects your listing and your revenue.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Property Type</Label>
                <Select value={formData.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="detached">Detached House</SelectItem>
                    <SelectItem value="semi_detached">Semi-Detached</SelectItem>
                    <SelectItem value="terraced">Terraced</SelectItem>
                    <SelectItem value="apartment">Apartment / Flat</SelectItem>
                    <SelectItem value="penthouse">Penthouse</SelectItem>
                    <SelectItem value="maisonette">Maisonette</SelectItem>
                    <SelectItem value="bungalow">Bungalow</SelectItem>
                    <SelectItem value="cottage">Cottage</SelectItem>
                    <SelectItem value="villa">Villa</SelectItem>
                    <SelectItem value="studio">Studio</SelectItem>
                    <SelectItem value="house">House (Other)</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="land">Land / Plot</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Listing Type</Label>
                <Select value={formData.listing_type} onValueChange={(v) => handleChange('listing_type', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-listing-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="sale">For Sale</SelectItem>
                    <SelectItem value="rent">For Rent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className={formData.property_type === 'land' || formData.property_type === 'commercial' ? 'hidden' : ''}>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Bedrooms</Label>
                <Select value={formData.bedrooms} onValueChange={(v) => handleChange('bedrooms', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-bedrooms"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className={formData.property_type === 'land' || formData.property_type === 'commercial' ? 'hidden' : ''}>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Bathrooms</Label>
                <Select value={formData.bathrooms} onValueChange={(v) => handleChange('bathrooms', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-bathrooms"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className={formData.property_type === 'land' || formData.property_type === 'commercial' ? 'col-span-3' : ''}>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">
                  {formData.property_type === 'land' ? 'Built-up Area (sqft) — optional' : 'Area (sqft)'}
                </Label>
                <Input type="number" value={formData.area_sqft} onChange={(e) => handleChange('area_sqft', e.target.value)} placeholder={formData.property_type === 'land' ? 'Leave blank for open plots' : '1500'}
                  className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-area" />
              </div>
            </div>

            {/* Plot / Land / Commercial specific fields */}
            {(formData.property_type === 'land' || formData.property_type === 'commercial') && (
              <div className="border-l-4 border-emerald-500 bg-emerald-50/60 p-5 space-y-4" data-testid="plot-fields-section">
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-emerald-700 font-bold mb-1">
                    {formData.property_type === 'land' ? 'Plot / Land Details' : 'Commercial Property Details'}
                  </p>
                  <p className="text-xs text-[#1A1A1A]/60">These details help buyers evaluate the plot quickly and build trust.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Plot Area *</Label>
                    <Input type="number" value={formData.plot_area} onChange={(e) => handleChange('plot_area', e.target.value)}
                      placeholder="e.g. 200"
                      className="bg-white border-slate-200 h-11 mt-1" data-testid="input-plot-area" required />
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Unit</Label>
                    <Select value={formData.plot_area_unit} onValueChange={(v) => handleChange('plot_area_unit', v)}>
                      <SelectTrigger className="bg-white border-slate-200 h-11 mt-1" data-testid="select-plot-unit"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="sqft">Sq Ft</SelectItem>
                        <SelectItem value="sqyd">Sq Yd (Gaj)</SelectItem>
                        <SelectItem value="sqm">Sq Metre</SelectItem>
                        <SelectItem value="acre">Acre</SelectItem>
                        <SelectItem value="hectare">Hectare</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Dimensions</Label>
                    <Input value={formData.plot_dimensions} onChange={(e) => handleChange('plot_dimensions', e.target.value)}
                      placeholder="e.g. 40 × 60 ft"
                      className="bg-white border-slate-200 h-11 mt-1" data-testid="input-plot-dimensions" />
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Facing</Label>
                    <Select value={formData.facing} onValueChange={(v) => handleChange('facing', v)}>
                      <SelectTrigger className="bg-white border-slate-200 h-11 mt-1" data-testid="select-facing"><SelectValue placeholder="Select direction" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="N">North</SelectItem>
                        <SelectItem value="S">South</SelectItem>
                        <SelectItem value="E">East</SelectItem>
                        <SelectItem value="W">West</SelectItem>
                        <SelectItem value="NE">North-East</SelectItem>
                        <SelectItem value="NW">North-West</SelectItem>
                        <SelectItem value="SE">South-East</SelectItem>
                        <SelectItem value="SW">South-West</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Approval / Authority</Label>
                    <Select value={formData.approval_authority} onValueChange={(v) => handleChange('approval_authority', v)}>
                      <SelectTrigger className="bg-white border-slate-200 h-11 mt-1" data-testid="select-approval"><SelectValue placeholder="Select approval" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="HMDA">HMDA (Hyderabad)</SelectItem>
                        <SelectItem value="DTCP">DTCP (Directorate of Town & Country Planning)</SelectItem>
                        <SelectItem value="GHMC">GHMC</SelectItem>
                        <SelectItem value="BBMP">BBMP (Bengaluru)</SelectItem>
                        <SelectItem value="BDA">BDA</SelectItem>
                        <SelectItem value="MMRDA">MMRDA (Mumbai)</SelectItem>
                        <SelectItem value="NOIDA">NOIDA / GNIDA</SelectItem>
                        <SelectItem value="CMDA">CMDA (Chennai)</SelectItem>
                        <SelectItem value="RERA">RERA Registered</SelectItem>
                        <SelectItem value="planning_permission">Planning Permission (UK)</SelectItem>
                        <SelectItem value="zoning">Zoning Approval (CA)</SelectItem>
                        <SelectItem value="gram_panchayat">Gram Panchayat</SelectItem>
                        <SelectItem value="unapproved">Unapproved</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Zoning</Label>
                    <Select value={formData.zoning} onValueChange={(v) => handleChange('zoning', v)}>
                      <SelectTrigger className="bg-white border-slate-200 h-11 mt-1" data-testid="select-zoning"><SelectValue placeholder="Select zoning" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="mixed">Mixed Use</SelectItem>
                        <SelectItem value="agricultural">Agricultural</SelectItem>
                        <SelectItem value="industrial">Industrial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* RERA Compliance Block — for India listings */}
                <div className="border border-[#D4AF37]/40 bg-[#FFF8E7] p-4 rounded-md" data-testid="rera-compliance-block">
                  <p className="text-[#0F172A] text-sm font-bold mb-1">India RERA Compliance</p>
                  <p className="text-[#1A1A1A]/70 text-xs mb-3">Required for new builder-projects under construction or recently completed (post-2017). Resale individual-owner properties are exempt — tick the box below if applicable.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">RERA Number</Label>
                      <Input
                        value={formData.rera_number || ''}
                        onChange={(e) => handleChange('rera_number', e.target.value)}
                        placeholder="e.g. P02400001234"
                        disabled={!!formData.rera_exempt}
                        className="bg-white border-slate-200 h-11 mt-1 disabled:opacity-50"
                        data-testid="input-rera-number"
                      />
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!formData.rera_exempt}
                          onChange={(e) => {
                            handleChange('rera_exempt', e.target.checked);
                            if (e.target.checked) handleChange('rera_number', '');
                          }}
                          className="mt-1"
                          data-testid="checkbox-rera-exempt"
                        />
                        <span className="text-[#0F172A] text-xs">
                          <b>I am an individual owner selling a resale property</b> (Exempt from RERA registration).
                        </span>
                      </label>
                    </div>
                  </div>
                  {!formData.rera_number && !formData.rera_exempt && (
                    <p className="text-[#B45309] text-xs mt-3 italic">
                      ⚠️ Listings without a valid RERA number (and not marked exempt) are flagged
                      "AT BUYER'S OWN RISK" on the public listing. Our AI will still sort and surface
                      your data — verification of legality remains the buyer's responsibility.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Survey / Plot No.</Label>
                    <Input value={formData.survey_number} onChange={(e) => handleChange('survey_number', e.target.value)}
                      placeholder="e.g. Sy. No. 123/4"
                      className="bg-white border-slate-200 h-11 mt-1" data-testid="input-survey-number" />
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold">Khata / Doc No.</Label>
                    <Input value={formData.khata_number} onChange={(e) => handleChange('khata_number', e.target.value)}
                      placeholder="e.g. Khata 567"
                      className="bg-white border-slate-200 h-11 mt-1" data-testid="input-khata-number" />
                  </div>
                </div>
              </div>
            )}
            <div>
              <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Price ({symbol} {currency}) *</Label>
              <Input type="number" value={formData.price} onChange={(e) => handleChange('price', e.target.value)} placeholder="5000000"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1 text-base" required data-testid="input-price" />
            </div>
          </div>

          {/* Location - Map Picker */}
          <div className="luxury-card p-6 space-y-5">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Location</h2>
            <p className="text-[#1A1A1A]/50 text-sm font-medium">Search a location or click on the map to set the property pin.</p>

            {/* Map Search */}
            <MapSearchBar onSelect={handleSearchSelect} />
            
            {/* Interactive Map */}
            <div className="h-80 border border-slate-200 overflow-hidden" data-testid="location-map-picker">
              <MapContainer 
                center={formData.latitude ? [parseFloat(formData.latitude), parseFloat(formData.longitude)] : [51.5074, -0.1278]}
                zoom={formData.latitude ? 14 : 5}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapClickHandler onLocationSelect={handleMapClick} />
                {flyTarget && <FlyToLocation position={flyTarget} />}
                {formData.latitude && formData.longitude && (
                  <Marker position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]} />
                )}
              </MapContainer>
            </div>

            {formData.latitude && (
              <div className="flex items-center gap-2 p-3 bg-[#FEF3EC]">
                <MapPin className="h-4 w-4 text-[#E07A3A]" />
                <span className="text-sm text-[#E07A3A] font-medium">
                  Location set: {parseFloat(formData.latitude).toFixed(4)}, {parseFloat(formData.longitude).toFixed(4)}
                </span>
                {reverseLoading && <Loader2 className="h-3 w-3 text-[#E07A3A] animate-spin ml-2" />}
              </div>
            )}

            <div>
              <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Address</Label>
              <Input value={formData.address} onChange={(e) => handleChange('address', e.target.value)} placeholder="Auto-filled from map or enter manually"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-address" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">City *</Label>
                <Input value={formData.city} onChange={(e) => handleChange('city', e.target.value)} placeholder="Auto-filled from map" required
                  className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-city" />
              </div>
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">State</Label>
                <Input value={formData.state} onChange={(e) => handleChange('state', e.target.value)} placeholder="Auto-filled from map"
                  className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-state" />
              </div>
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">PIN Code</Label>
                <Input value={formData.zip_code} onChange={(e) => handleChange('zip_code', e.target.value)} placeholder="Auto-filled"
                  className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-zip" />
              </div>
            </div>
          </div>

          {/* Images - File Upload */}
          <div className="luxury-card p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] font-semibold">Property Images</h2>
              <button type="button" onClick={loadImageTips}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#E07A3A] hover:text-[#C4652E] transition-colors"
                data-testid="photo-tips-btn">
                <Camera className="h-3 w-3" />
                {showImageTips ? 'Hide Tips' : 'Pro Photo Tips'}
              </button>
            </div>

            {/* Photography Tips */}
            {showImageTips && imageTips && (
              <div className="bg-[#0F172A] p-5 space-y-3" data-testid="photo-tips-panel">
                <p className="text-[#E07A3A] text-xs tracking-[0.2em] uppercase font-bold mb-2">Photography Guide</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {imageTips.tips?.map((tip, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-7 h-7 flex-shrink-0 bg-white/10 flex items-center justify-center text-[#E07A3A]">
                        {tip.icon === 'sun' && <Sun className="h-3.5 w-3.5" />}
                        {tip.icon === 'sparkles' && <Sparkles className="h-3.5 w-3.5" />}
                        {tip.icon === 'maximize' && <Maximize2 className="h-3.5 w-3.5" />}
                        {tip.icon === 'camera' && <Camera className="h-3.5 w-3.5" />}
                        {tip.icon === 'home' && <HomeIcon className="h-3.5 w-3.5" />}
                        {tip.icon === 'smartphone' && <Smartphone className="h-3.5 w-3.5" />}
                      </div>
                      <div>
                        <p className="text-white text-xs font-semibold">{tip.title}</p>
                        <p className="text-white/40 text-xs leading-relaxed font-medium mt-0.5">{tip.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {imageTips.panoramic_tips && (
                  <div className="border-t border-white/10 pt-3 mt-3 flex gap-3 items-start">
                    <div className="w-7 h-7 flex-shrink-0 bg-[#E07A3A]/20 flex items-center justify-center text-[#E07A3A]">
                      <Search className="h-3.5 w-3.5" />
                    </div>
                    <div>
                      <p className="text-[#E07A3A] text-xs font-semibold">{imageTips.panoramic_tips.title}</p>
                      <p className="text-white/40 text-xs leading-relaxed font-medium mt-0.5">{imageTips.panoramic_tips.description}</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <div>
              <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold mb-2 block">Upload Photos</Label>
              <input type="file" accept="image/*" multiple ref={imageInputRef} onChange={handleImageUpload} className="hidden" />
              <button type="button" onClick={() => imageInputRef.current?.click()} disabled={uploading}
                className="w-full border-2 border-dashed border-slate-200 hover:border-[#E07A3A] p-8 flex flex-col items-center gap-2 transition-colors" data-testid="upload-images-btn">
                {uploading ? <Loader2 className="h-8 w-8 text-[#E07A3A] animate-spin" /> : <Upload className="h-8 w-8 text-[#E07A3A]" />}
                <span className="text-[#1A1A1A]/60 text-sm font-medium">{uploading ? 'Uploading...' : 'Click to upload property photos (max 20MB each)'}</span>
              </button>

              {/* QR Photo Bridge */}
              <div className="mt-3">
                <PhotoBridge onPhotosReceived={handleBridgePhotos} />
              </div>
              
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {formData.images.map((url, idx) => (
                    <div key={`img-${url.slice(-10)}-${idx}`} className="relative w-24 h-24 bg-slate-100 overflow-hidden group">
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(idx)}
                        className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold mb-2 block">360° Panoramic Image</Label>
              <input type="file" accept="image/*" ref={panoInputRef} onChange={handlePanoUpload} className="hidden" />
              <div className="flex gap-3">
                <button type="button" onClick={() => panoInputRef.current?.click()} disabled={uploading}
                  className="flex-1 border-2 border-dashed border-slate-200 hover:border-[#E07A3A] p-4 flex items-center justify-center gap-2 transition-colors" data-testid="upload-pano-btn">
                  <Image className="h-5 w-5 text-[#E07A3A]" />
                  <span className="text-[#1A1A1A]/60 text-sm font-medium">{formData.panoramic_image ? 'Change 360° Image' : 'Upload 360° Image'}</span>
                </button>
              </div>
              {formData.panoramic_image && (
                <div className="mt-3 flex items-center gap-2 p-2 bg-[#FEF3EC]">
                  <img src={formData.panoramic_image} alt="360°" className="w-16 h-16 object-cover" />
                  <span className="text-sm text-[#E07A3A] font-medium flex-1">360° image uploaded</span>
                  <button type="button" onClick={() => setFormData({...formData, panoramic_image: ''})} className="text-red-500 hover:text-red-700">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Amenities */}
          <div className="luxury-card p-6 space-y-5">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Amenities</h2>
            <div className="flex gap-2">
              <Input value={newAmenity} onChange={(e) => setNewAmenity(e.target.value)} placeholder="e.g. Swimming Pool, Gym, Parking"
                className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAmenity())} data-testid="input-amenity" />
              <Button type="button" onClick={addAmenity} className="bg-[#E07A3A] text-white hover:bg-[#C4652E] h-12 px-6"><Plus className="h-4 w-4" /></Button>
            </div>
            {formData.amenities.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.amenities.map((a, idx) => (
                  <span key={`amenity-${a}-${idx}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#FEF3EC] text-[#E07A3A] text-sm font-semibold">
                    {a}
                    <button type="button" onClick={() => removeAmenity(idx)} className="ml-1 hover:text-[#C4652E]"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tenure & Legal (Sale listings) */}
          {formData.listing_type === 'sale' && (
            <div className="luxury-card p-6 space-y-5" data-testid="tenure-section">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Tenure & Legal</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Tenure *</Label>
                  <Select value={formData.tenure} onValueChange={(v) => handleChange('tenure', v)}>
                    <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-tenure"><SelectValue placeholder="Select tenure" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="freehold">Freehold</SelectItem>
                      <SelectItem value="leasehold">Leasehold</SelectItem>
                      <SelectItem value="share_of_freehold">Share of Freehold</SelectItem>
                      <SelectItem value="commonhold">Commonhold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(formData.tenure === 'leasehold' || formData.tenure === 'share_of_freehold') && (
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Lease Years Remaining</Label>
                    <Input type="number" value={formData.lease_years_remaining} onChange={(e) => handleChange('lease_years_remaining', e.target.value)} placeholder="e.g. 85"
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-lease-years" />
                  </div>
                )}
              </div>
              {(formData.tenure === 'leasehold' || formData.tenure === 'share_of_freehold') && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Ground Rent ({symbol}/yr)</Label>
                    <Input type="number" value={formData.ground_rent} onChange={(e) => handleChange('ground_rent', e.target.value)} placeholder="e.g. 200"
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-ground-rent" />
                  </div>
                  <div>
                    <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Service Charge ({symbol}/yr)</Label>
                    <Input type="number" value={formData.service_charge} onChange={(e) => handleChange('service_charge', e.target.value)} placeholder="e.g. 1500"
                      className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-service-charge" />
                  </div>
                </div>
              )}
              {formData.tenure === 'leasehold' && formData.lease_years_remaining && parseInt(formData.lease_years_remaining) < 80 && (
                <div className="p-3 bg-red-50 border border-red-200 flex items-start gap-2" data-testid="short-lease-warning">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-red-700">Short lease warning: Under 80 years remaining. Buyers may struggle to get a mortgage. Consider extending the lease before sale.</p>
                </div>
              )}
            </div>
          )}

          {/* Rental Details (Rent listings) */}
          {formData.listing_type === 'rent' && (
            <div className="luxury-card p-6 space-y-5" data-testid="rental-details-section">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Rental Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Furnished Status</Label>
                  <Select value={formData.furnished_status} onValueChange={(v) => handleChange('furnished_status', v)}>
                    <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-furnished"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="furnished">Furnished</SelectItem>
                      <SelectItem value="part_furnished">Part Furnished</SelectItem>
                      <SelectItem value="unfurnished">Unfurnished</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Deposit ({symbol})</Label>
                  <Input type="number" value={formData.deposit_amount} onChange={(e) => handleChange('deposit_amount', e.target.value)} placeholder="e.g. 1500"
                    className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-deposit" />
                </div>
              </div>
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Available From</Label>
                <Input type="date" value={formData.available_from} onChange={(e) => handleChange('available_from', e.target.value)}
                  className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="input-available-from" />
              </div>
            </div>
          )}

          {/* EPC & Council Tax */}
          <div className="luxury-card p-6 space-y-5" data-testid="epc-section">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">EPC & Council Tax</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">EPC Rating</Label>
                <Select value={formData.epc_rating} onValueChange={(v) => handleChange('epc_rating', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-epc"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G'].map(r => (
                      <SelectItem key={r} value={r}>{r} {getEpcLabel(r)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#1A1A1A]/70 text-sm tracking-wider uppercase font-semibold">Council Tax Band</Label>
                <Select value={formData.council_tax_band} onValueChange={(v) => handleChange('council_tax_band', v)}>
                  <SelectTrigger className="bg-[#F8FAFC] border-slate-200 text-[#1A1A1A] h-12 mt-1" data-testid="select-council-tax"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map(b => <SelectItem key={b} value={b}>Band {b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Floor Plan Upload */}
          <div className="luxury-card p-6 space-y-5" data-testid="floor-plan-section">
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Floor Plan</h2>
            <p className="text-[#1A1A1A]/50 text-sm font-medium">Upload existing floor plans or let AI generate one. Buyers rank floor plans as the #1 most useful feature.</p>

            {/* AI Floor Plan Generator */}
            <div className="border border-dashed border-[#E07A3A]/30 bg-[#FEF3EC]/30 p-4">
              <AIFloorPlanGenerator
                propertyType={formData.property_type}
                bedrooms={formData.bedrooms}
                bathrooms={formData.bathrooms}
                areaSqft={formData.area_sqft}
                country={formData.country || 'UK'}
                initialFloors={formData.floor_plans || []}
                onFloorPlansChange={(next) => setFormData(prev => ({
                  ...prev,
                  floor_plans: next,
                  // Keep floor_plan_images in sync for legacy views
                  floor_plan_images: next.filter(f => f.enhanced_url).map(f => f.enhanced_url),
                }))}
                onFloorPlanGenerated={(url) => setFormData(prev => prev.floor_plan_images.includes(url) ? prev : ({ ...prev, floor_plan_images: [...prev.floor_plan_images, url] }))}
              />
            </div>

            {/* Manual Upload */}
            <div className="pt-2">
              <Label className="text-[#1A1A1A]/70 text-xs tracking-wider uppercase font-semibold mb-2 block">Or upload your own floor plan</Label>
            <input type="file" accept="image/*,.pdf" multiple ref={floorPlanInputRef} onChange={handleFloorPlanUpload} className="hidden" />
            <button type="button" onClick={() => floorPlanInputRef.current?.click()} disabled={uploading}
              className="w-full border-2 border-dashed border-slate-200 hover:border-[#E07A3A] p-6 flex flex-col items-center gap-2 transition-colors" data-testid="upload-floorplan-btn">
              {uploading ? <Loader2 className="h-6 w-6 text-[#E07A3A] animate-spin" /> : <Maximize2 className="h-6 w-6 text-[#E07A3A]" />}
              <span className="text-[#1A1A1A]/60 text-sm font-medium">{uploading ? 'Uploading...' : 'Upload Floor Plans (images or PDF)'}</span>
            </button>
            {formData.floor_plan_images.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3">
                {formData.floor_plan_images.map((url, idx) => (
                  <div key={`fp-${idx}`} className="relative w-28 h-28 bg-slate-100 overflow-hidden group border border-slate-200">
                    <img src={url} alt={`Floor plan ${idx + 1}`} className="w-full h-full object-contain" />
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, floor_plan_images: prev.floor_plan_images.filter((_, i) => i !== idx) }))}
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>

          {/* Owner Contact (Agent listings only) */}
          {isAgent && (
            <div className="luxury-card p-6" data-testid="owner-contact-section">
              <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2 font-semibold">Property Owner Details</h2>
              <p className="text-sm text-slate-400 mb-4">The property owner will be notified if your trial expires. Their details are kept confidential.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[#1A1A1A]/60 text-xs tracking-wider uppercase">Owner Name</Label>
                  <Input value={formData.owner_name} onChange={(e) => handleChange('owner_name', e.target.value)}
                    placeholder="e.g. Jane Smith" className="bg-white h-11 mt-1" data-testid="owner-name-input" />
                </div>
                <div>
                  <Label className="text-[#1A1A1A]/60 text-xs tracking-wider uppercase">Owner Email</Label>
                  <Input type="email" value={formData.owner_email} onChange={(e) => handleChange('owner_email', e.target.value)}
                    placeholder="owner@email.com" className="bg-white h-11 mt-1" data-testid="owner-email-input" />
                </div>
                <div>
                  <Label className="text-[#1A1A1A]/60 text-xs tracking-wider uppercase">Owner Phone</Label>
                  <Input value={formData.owner_phone} onChange={(e) => handleChange('owner_phone', e.target.value)}
                    placeholder="07700 000000" className="bg-white h-11 mt-1" data-testid="owner-phone-input" />
                </div>
              </div>
            </div>
          )}

          {/* Seller Disclaimer */}
          <div className="p-4 bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>Seller Declaration:</strong> By listing this property, you confirm all details are accurate to the best of your knowledge. Shopperden acts as a property introduction service only. 
              Final sale/tenancy agreements are between parties directly. You are responsible for providing accurate documentation to buyers/tenants during the verification process.
            </p>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-[#E07A3A] text-white hover:bg-[#C4652E] h-14 text-base tracking-widest uppercase font-semibold" data-testid="submit-property-btn">
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isEditing ? 'Updating...' : 'Creating...'}</>
            ) : (
              isEditing ? 'Update Property' : 'Publish Listing'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default CreatePropertyPage;
